import { PAYMENT_METHODS } from 'src/common/enums';
export * from './cardconnect';
export * from './cybersource';

export interface BreakdownItem {
  label: string;
  value: number;
  key?: string;
}

export interface PaymentFeesSummary {
  total?: number;
  credit?: number;
}

export interface PaymentFees {
  ccFee?: number | null;
  ccTotal?: number | null;
  achFee?: number | null;
  achTotal?: number | null;
  checkFee?: number | null;
  checkTotal?: number | null;
  breakdown?: BreakdownItem[];
  summary?: PaymentFeesSummary;
  signature?: string;
}

export interface PaymentSuccessInfo {
  id?: string;
  paymentConfirmation?: string;
}

export interface PaymentState {
  isInitialized?: boolean;
  isLoading?: boolean;
  paymentCheckFailed?: boolean;
  paymentFees?: PaymentFees;
  paymentID?: number | string | null;
  paymentFailed?: boolean;
  paymentSuccesInfo?: PaymentSuccessInfo | null;
  formData?: unknown; // Shared information across be/fe depends on payment provider
  selectedPaymentMethod?: PAYMENT_METHODS;
  signatoryName?: string | null;
  status?: string;
}

export enum PAYMENT_PROCESSOR {
  GENERIC = 'generic',
  STRIPE = 'stripe',
}

export enum PAYMENT_CATEGORY {
  APPLICATION = 'application_fee',
  RENEWAL = 'renewal_fee',
  TOT = 'tot',
}

export enum PAYMENT_PROVIDER {
  AUTOAGENT = 'autoagent',
  CARD_CONNECT = 'cardconnect',
  CERTIFIED_PAYMENTS = 'certifiedpayments',
  CYBERSOURCE = 'cybersource',
  FULLPAY = 'fullpay',
  HAMER = 'hamer',
  HEARTLAND = 'heartland',
  INTELLIPAY = 'intellipay',
  INVOICE_CLOUD = 'invoicecloud',
  KALA = 'kala',
  POINT_AND_PAY = 'pointandpay',
  SNAPPAY = 'snappay',
  XPRESS_BILL_PAY = 'xpressbillpay',
}

export type PaymentMethod =
  (typeof PAYMENT_METHODS)[keyof typeof PAYMENT_METHODS];

export const PAYMENT_SOURCE = 'registration_system' as const;

// ─── Base request interfaces ──────────────────────────────────────────────────

export interface BaseSignatoryRequest {
  email: string;
  payment_id: number;
  signatory: string;
  readonly source: typeof PAYMENT_SOURCE;
  subtotal: number | string;
  // Covered by the fee signature for every payment method, so it is required even where the
  // endpoint does not act on it. Send 0 when the payment has no credit.
  credits: number;
}

export interface BasePaymentRequest extends BaseSignatoryRequest {
  payment_category: PAYMENT_CATEGORY;
  permit_numbers?: string[];
  signature: string;
}

// ─── Request interfaces ───────────────────────────────────────────────────────

export interface ACHDirectDebitPaymentRequest extends BasePaymentRequest {}

export interface CheckPaymentRequest extends BasePaymentRequest {
  unique_code?: string | null;
}

export interface CreditCardPaymentRequest extends BasePaymentRequest {}

// ─── Provider-specific payload types ─────────────────────────────────────────

export interface RegistrantPayload {
  first_name?: string;
  last_name?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
}

export interface CertifiedPaymentsPaymentPeriod {
  amount: number;
  license: string;
  formatted_period: string;
  fee_type: PAYMENT_CATEGORY;
  address?: string;
  tax_due?: number;
  late_penalties?: number;
  accrued_interest?: number;
}

export interface CertifiedPaymentsPayload {
  registrant?: RegistrantPayload;
  payment_periods: CertifiedPaymentsPaymentPeriod[];
}

export interface HeartlandPayload {
  registrant?: RegistrantPayload;
}

export interface FullPayPaymentPeriod {
  license_id: string;
  license_number: string;
  formatted_period: string;
}

export interface FullPayPayload {
  payment_periods: FullPayPaymentPeriod[];
  booking_service_unique_code?: string;
}

export interface KalaPayload {
  payment_method: 'credit_card' | 'ach';
}

export type GenericProviderPayload =
  | CertifiedPaymentsPayload
  | HeartlandPayload
  | FullPayPayload
  | KalaPayload
  | null;

export interface GenericPaymentRequest extends BasePaymentRequest {
  payload?: GenericProviderPayload;
}

export interface PayNothingRequest extends BaseSignatoryRequest {
  subtotal: 0;
  signature: string;
  payment_category: PAYMENT_CATEGORY;
}

// ─── Response interfaces ──────────────────────────────────────────────────────

export interface ACHDirectDebitPaymentResponse {
  clientSecret: string;
  paymentId: number;
}

export interface ChequePaymentResponse {
  paymentId: number;
  checkMailingAddress: string;
  checkContactEmail: string;
  checkUniqueCode: string;
  checkPayTo: string;
}

export interface CheckPaymentStatusResponse {
  /** @deprecated v1 only */
  id?: string;
  paymentConfirmation?: string;
  paymentId?: number;
  status?: PostbackStatus;
  errorMessage?: string | null;
}

export interface CreditCardPaymentResponse {
  clientSecret: string;
  paymentId: number;
}

export interface GenericPaymentsResponse {
  checkout_mode?: 'post_redirect' | 'redirect';
  formData: Record<string, unknown>;
  url: string;
}

export enum PostbackStatus {
  /** @deprecated This is used in the legacy API - This is replaced by COMPLETE in the new model */
  AUTHORIZED = 'AUTHORIZED',
  COMPLETE = 'complete',
  FAILED = 'failed',
  PENDING = 'pending',
  /** @deprecated This is used in the legacy API - This is replaced by COMPLETE in the new model */
  SUCCESS = 'success',
  UNSUBMITTED = 'unsubmitted',
}

export interface PostbackResponse {
  errorMessage?: string;
  paymentId: number;
  status: PostbackStatus;
}

export type PaymentApiResponse =
  | ACHDirectDebitPaymentResponse
  | ChequePaymentResponse
  | CreditCardPaymentResponse
  | GenericPaymentsResponse;

export interface PayNothingResponse {
  paymentId: number;
  status: PostbackStatus;
}

export interface StripePublicKeyResponse {
  stripePublishableKey: string;
  totStripePublishableKey: string | null;
}

// ─── Deprecated ───────────────────────────────────────────────────────────────

/** @deprecated Use BaseSignatoryRequest once API is fully transitioned to v2 */
export interface BaseSignatoryRequestOld {
  email: string;
  paymentId: string;
  signatory: string;
}

/** @deprecated Use ACHDirectDebitPaymentRequest once API is fully transitioned to v2 */
export interface ACHDirectDebitPaymentRequestOld
  extends BaseSignatoryRequestOld {}

/** @deprecated Use CheckPaymentRequest once API is fully transitioned to v2 */
export interface CheckPaymentRequestOld extends BaseSignatoryRequestOld {}

/** @deprecated Use CreditCardPaymentRequest once API is fully transitioned to v2 */
export interface CreditCardPaymentRequestOld extends BaseSignatoryRequestOld {}

/** @deprecated Use GenericPaymentRequest once API is fully transitioned to v2 */
export interface GenericPaymentRequestOld extends BaseSignatoryRequestOld {}

/** @deprecated Use PayNothingRequest once API is fully transitioned to v2 */
export interface PayNothingRequestOld extends BaseSignatoryRequestOld {}

/** @deprecated Use StripePublicKeyResponse once API is fully transitioned to v2 */
export interface StripePublicKeyResponseOld {
  stripePublishableKey: string;
  tot?: {
    stripePublishableKey: string;
  };
}
